<template>
  <div class="congruence-similarity-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg viewBox="-200 -200 400 400" class="canvas-svg">
          <!-- Circumcircle -->
          <circle cx="0" cy="0" r="100" fill="none" stroke="#64748b" stroke-width="1.5" stroke-dasharray="2,2" />
          
          <!-- Triangle ABC -->
          <polygon :points="`${ptA.x},${ptA.y} ${ptB.x},${ptB.y} ${ptC.x},${ptC.y}`" fill="rgba(13, 104, 255, 0.1)" stroke="#0D68FF" stroke-width="2" />
          <text :x="ptA.x*1.15" :y="ptA.y*1.15" fill="#8ba1ff" font-size="14" font-weight="bold" text-anchor="middle">A</text>
          <text :x="ptB.x*1.15" :y="ptB.y*1.15" fill="#8ba1ff" font-size="14" font-weight="bold" text-anchor="middle">B</text>
          <text :x="ptC.x*1.15" :y="ptC.y*1.15" fill="#8ba1ff" font-size="14" font-weight="bold" text-anchor="middle">C</text>

          <!-- Simson Line (calculated and extended for visual clarity) -->
          <line :x1="simsonL.x1" :y1="simsonL.y1" :x2="simsonL.x2" :y2="simsonL.y2" stroke="#2AF598" stroke-width="2" />

          <!-- Perpendiculars -->
          <line :x1="ptP.x" :y1="ptP.y" :x2="footAB.x" :y2="footAB.y" stroke="#94a3b8" stroke-dasharray="4" />
          <line :x1="ptP.x" :y1="ptP.y" :x2="footBC.x" :y2="footBC.y" stroke="#94a3b8" stroke-dasharray="4" />
          <line :x1="ptP.x" :y1="ptP.y" :x2="footCA.x" :y2="footCA.y" stroke="#94a3b8" stroke-dasharray="4" />

          <!-- P point -->
          <circle :cx="ptP.x" :cy="ptP.y" r="5" fill="#facc15" />
          <text :x="ptP.x*1.2" :y="ptP.y*1.2" fill="#facc15" font-size="16" font-weight="bold" text-anchor="middle">P</text>

          <!-- Feet Points -->
          <circle :cx="footAB.x" :cy="footAB.y" r="4" fill="#2AF598" />
          <circle :cx="footBC.x" :cy="footBC.y" r="4" fill="#2AF598" />
          <circle :cx="footCA.x" :cy="footCA.y" r="4" fill="#2AF598" />
          <text :x="footAB.x+10" :y="footAB.y+10" fill="#2AF598" font-size="12">F</text>
          <text :x="footBC.x+10" :y="footBC.y+10" fill="#2AF598" font-size="12">D</text>
          <text :x="footCA.x+10" :y="footCA.y+10" fill="#2AF598" font-size="12">E</text>
        </svg>

        <div class="hud-center">
          <div class="status-badge con">
             P 向三边作垂线，垂足必定共线！
          </div>
        </div>
      </div>

      <div class="compact-controls">
         <div class="control-card glass">
            <div class="control-vertical-group">
               <div class="param-row">
                  <div class="s-item">
                     <label>游动点 P 位置</label>
                     <el-slider v-model="angleP" :min="0" :max="360" :step="1" input-size="mini"></el-slider>
                  </div>
               </div>
            </div>
         </div>
         <div class="discovery-card-compact" v-latex>
            <i>🔍</i>
            <p><strong>西摩松定理：</strong>在这美妙的几何性质中，只要点 P 在三角形的外接圆上游动，它到三角形三条边（或其延长线）的垂足必然永远落在同一条直线上（这就是西摩松线）。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'SimsonLineViz',
  data() {
    return {
      angleP: 200,
      ptA: this.getPolar(90, 100),
      ptB: this.getPolar(210, 100),
      ptC: this.getPolar(330, 100)
    }
  },
  computed: {
    ptP() { return this.getPolar(this.angleP, 100); },
    footAB() { return this.getFoot(this.ptP, this.ptA, this.ptB); },
    footBC() { return this.getFoot(this.ptP, this.ptB, this.ptC); },
    footCA() { return this.getFoot(this.ptP, this.ptC, this.ptA); },
    simsonL() {
      // Find two points that are far apart enough for a nice line
      let d1 = Math.pow(this.footAB.x - this.footBC.x, 2) + Math.pow(this.footAB.y - this.footBC.y, 2);
      let d2 = Math.pow(this.footBC.x - this.footCA.x, 2) + Math.pow(this.footBC.y - this.footCA.y, 2);
      let p1 = this.footAB;
      let p2 = this.footBC;
      if (d2 > d1) { p1 = this.footBC; p2 = this.footCA; }
      
      let dx = p2.x - p1.x;
      let dy = p2.y - p1.y;
      return {
        x1: p1.x - dx*2, y1: p1.y - dy*2,
        x2: p1.x + dx*3, y2: p1.y + dy*3
      };
    }
  },
  methods: {
    getPolar(deg, r) {
      let rad = deg * Math.PI / 180;
      return { x: r * Math.cos(rad), y: -r * Math.sin(rad) };
    },
    getFoot(p, a, b) {
      let dx = b.x - a.x;
      let dy = b.y - a.y;
      if (Math.abs(dx) < 0.0001 && Math.abs(dy) < 0.0001) return {x: p.x, y: p.y};
      let u = ((p.x - a.x)*dx + (p.y - a.y)*dy) / (dx*dx + dy*dy);
      // Let's cap U if needed? No, Simson theorem relies on extended lines!
      return { x: a.x + u*dx, y: a.y + u*dy };
    }
  }
}
</script>

<style scoped lang="scss">
.congruence-similarity-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #10b981; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.main-layout { display: flex; gap: 12px; height: 100%; min-height: 0; flex-direction: column; }
.simulation-stage { 
  flex: 1; min-height: 200px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .canvas-svg { width: 100%; height: 100%; }
  .hud-center {
    position: absolute; top: 15px; width: 100%; text-align: center; display: flex; justify-content: center;
    .status-badge {
      background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); padding: 4px 16px; border-radius: 20px; color: #fff; font-weight: bold; font-size: 13px; backdrop-filter: blur(4px);
      &.con { background: rgba(42,245,152,0.2); border-color: #2AF598; color: #34d399; }
    }
  }
}
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .param-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: -5px; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { font-style: normal; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
